import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { trackConsent } from "@/lib/apis";


const GROMOR_CONSENTS = [
    {
    id: "bureau",
    label: "Credit Bureau Check",
    trackingKey: "Bureau Consent",
    description:
      "I further authorize Gromor Finance Private Limited to obtain/pull my CIBIL report and any other credit information from credit bureaus for credit assessment, verification, and underwriting purposes.",
  },
  {
    id: "dataSharing",
    label: "Information Sharing",
    trackingKey: "Data Sharing",
    description:
      "I provide my explicit consent to Gromor Finance Private Limited to share my information with its authorized stakeholders, on a need-to-know basis, solely for loan processing, underwriting, servicing, and compliance with applicable laws.",
  },
  {
    id: "kyc",
    label: "KYC & Transaction Details",
    trackingKey: "KYC Consent",
    description:
      "I hereby provide my explicit consent to Gromor Finance Private Limited to access, receive, and verify my KYC information and platform transaction details for the purpose of evaluating, processing, and offering financial products and services",
  },

  
];

export default function GromorConsentDialog({
  isOpen,
  onAllConsentsAgreed,
  lender,
}) {
  const [consents, setConsents] = useState({
    kyc: true,
    bureau: true,
    dataSharing: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allConsentsChecked = Object.values(consents).every(Boolean);

  const handleContinue = async () => {
    if (!allConsentsChecked) return;

    setIsSubmitting(true);
    try {
      const platform =
        navigator?.userAgentData?.platform ||
        navigator.platform ||
        localStorage.getItem("UTM_SOURCE") ||
        "web";

      // Recorded before the tenant leaves for Digio/DigiLocker. Awaited, not fired and
      // forgotten: the bureau consent is what lets the enquiry convert, and leaving without
      // it is exactly the failure this box exists to end.
      await Promise.allSettled(
        GROMOR_CONSENTS.map((consent) =>
          trackConsent(
            "Checkbox",
            consent.trackingKey,
            "",
            platform,
            consent.description,
          ).catch((error) => {
            console.error(`Failed to track ${consent.trackingKey}:`, error);
            return null;
          }),
        ),
      );

      onAllConsentsAgreed();
    } catch (error) {
      console.error("Error tracking Gromor consents:", error);
      // The tenant is not held hostage to our bookkeeping: cpcore re-drives the push on every
      // later consent, so let them continue rather than trapping them in this dialog.
      onAllConsentsAgreed();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent className="max-w-sm mx-auto bg-white border border-gray-200">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-base font-semibold text-gray-900">
            Terms &amp; Consents
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {GROMOR_CONSENTS.map((consent) => (
            <div key={consent.id} className="flex items-start gap-3">
              <input
                type="checkbox"
                id={`gromor-consent-${consent.id}`}
                checked={consents[consent.id]}
                onChange={(e) =>
                  setConsents((prev) => ({
                    ...prev,
                    [consent.id]: e.target.checked,
                  }))
                }
                className="accent-[#915DA5] w-4 h-4 border-gray-300 rounded"
              />
              <div className="flex-1 min-w-0">
                <label
                  htmlFor={`gromor-consent-${consent.id}`}
                  className="text-xs font-medium text-gray-900 cursor-pointer block mb-1"
                >
                  {consent.label}
                </label>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {consent.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex pt-4 border-t border-gray-100 flex-col gap-2">
          <Button
            onClick={handleContinue}
            disabled={!allConsentsChecked || isSubmitting}
            className="w-full text-xs h-9 bg-[#915DA5] hover:bg-[#7c4791] text-white"
          >
            {isSubmitting ? "Processing..." : "Continue"}
          </Button>
          {lender?.logoUrl && (
            <div className="flex items-center justify-center">
              <span className="text-black text-2xs italic">powered by</span>
              <img
                src={lender.logoUrl}
                alt={lender.legalName}
                className="inline-block max-h-3 ml-1"
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
